import React from 'react';
import { motion } from 'framer-motion';

const TechStack = () => {
  const techCategories = [
    {
      category: 'AI Models & Agentic Stack',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/>
        </svg>
      ),
      span: 'lg:col-span-4',
      accent: '#D946EF',
      description: 'LLM orchestration, custom fine-tuning, and RAG pipelines.',
      skills: [
        { name: 'OpenAI GPT-4o', level: 'Enterprise Integration' },
        { name: 'Anthropic Claude 3.5', level: 'Complex Reasoning' },
        { name: 'DeepSeek R1', level: 'Open Weights Logic' },
        { name: 'Llama 3.3', level: 'Local Privacy Ops' },
        { name: 'LangChain & LlamaIndex', level: 'RAG & Vector Search' },
      ],
    },
    {
      category: 'Fullstack Software Engineering',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        </svg>
      ),
      span: 'lg:col-span-4',
      accent: '#8B5CF6',
      description: 'Modern, high-performance web applications and API microservices.',
      skills: [
        { name: 'React 19 & Next.js', level: 'Frontend Framework' },
        { name: 'Node.js & Express', level: 'Scalable Microservices' },
        { name: 'Python & FastAPI', level: 'AI Backend & Data Ops' },
        { name: 'TypeScript', level: 'End-to-End Type Safety' },
        { name: 'TailwindCSS & Framer', level: 'Fluid Modern UI' },
      ],
    },
    {
      category: 'Cloud Infra & High-Availability',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>
        </svg>
      ),
      span: 'lg:col-span-4',
      accent: '#00F0FF',
      description: 'Resilient cloud infrastructure engineered for 99.98% uptime.',
      skills: [
        { name: 'Amazon Web Services (AWS)', level: 'Cloud Compute & S3' },
        { name: 'Docker & Kubernetes', level: 'Container Orchestration' },
        { name: 'PostgreSQL & MongoDB', level: 'Relational & NoSQL' },
        { name: 'Redis Cache', level: 'Sub-millisecond Latency' },
        { name: 'Vercel & Cloudflare', level: 'Global Edge Delivery' },
      ],
    },
  ];

  return (
    <section id="tech" className="py-24 bg-[#060C1B] relative overflow-hidden text-white">
      {/* Ambient glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-fuchsia-600/8 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-cyan-500/6 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="section-label-dark mb-3 justify-center">Engineered Infrastructure</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mb-4">
            Battle-tested technologies powering{' '}
            <span className="gradient-text">our builds.</span>
          </h2>
          <p className="text-slate-300 text-base font-normal">
            We use industry-leading frameworks, cloud providers, and foundation models to ensure speed, security, and scalability.
          </p>
        </div>

        {/* Tech Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {techCategories.map((cat, idx) => (
            <motion.div
              key={cat.category}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: idx * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className={`bento-card p-8 flex flex-col justify-between bg-[#0B132B] ${cat.span}`}
            >
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center border"
                    style={{
                      backgroundColor: `${cat.accent}15`,
                      borderColor: `${cat.accent}35`,
                      color: cat.accent,
                    }}
                  >
                    {cat.icon}
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-base text-white leading-snug">
                      {cat.category}
                    </h3>
                  </div>
                </div>

                <p className="text-xs text-slate-400 mb-6 font-normal leading-relaxed">
                  {cat.description}
                </p>

                {/* Tech Badges List */}
                <div className="space-y-2.5">
                  {cat.skills.map((skill) => (
                    <div
                      key={skill.name}
                      className="p-3 rounded-xl bg-slate-900/70 border border-white/5 flex items-center justify-between hover:border-fuchsia-500/25 hover:bg-slate-900 transition-all duration-200 group/skill"
                    >
                      <span className="font-heading font-semibold text-sm text-slate-200 group-hover/skill:text-white transition-colors">
                        {skill.name}
                      </span>
                      <span className="text-[10px] font-mono text-slate-500 group-hover/skill:text-slate-400 transition-colors">
                        {skill.level}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-white/5 flex items-center gap-2 text-xs font-mono text-slate-400">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ boxShadow: '0 0 6px #4ade80' }} />
                <span>Audited Architecture Standards</span>
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default TechStack;
